import type { DeckCard } from "@hyggebam/shared";
import { useRef, useState } from "react";
import type { SwipeDirection } from "@hyggebam/shared";
import { api } from "../api.ts";
import { RatingBadges } from "./RatingBadges.tsx";
import { TrailerLightbox, extractYouTubeId } from "./TrailerLightbox.tsx";

const THRESHOLD = 90; // px before a flick commits

/**
 * A draggable movie card.
 * up = never ("don't want to see it"), left = later ("not now"),
 * right = like, down = "seen it" (watched).
 *
 * The poster is a dimmed full-bleed backdrop: the top ~quarter stays bright,
 * then a scrim darkens the lower ~75% so the plot reads clearly. The trailer
 * key isn't on the deck payload, so we lazy-fetch it on first tap.
 */
export function SwipeCard({
  card,
  token,
  interactive,
  depth,
  onSwipe,
  onDetails,
}: {
  card: DeckCard;
  token: string;
  interactive: boolean;
  depth: number;
  onSwipe: (dir: SwipeDirection) => void;
  onDetails?: (tmdbId: number) => void;
}) {
  const [drag, setDrag] = useState({ x: 0, y: 0 });
  const [leaving, setLeaving] = useState<SwipeDirection | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  // Trailer: youtube id resolved lazily (null = unknown, "" = none found).
  const [trailerId, setTrailerId] = useState<string | null>(null);
  const [trailerOpen, setTrailerOpen] = useState(false);
  const [trailerBusy, setTrailerBusy] = useState(false);

  function down(e: React.PointerEvent) {
    if (!interactive || leaving) return;
    start.current = { x: e.clientX, y: e.clientY };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }
  function move(e: React.PointerEvent) {
    if (!start.current) return;
    setDrag({ x: e.clientX - start.current.x, y: e.clientY - start.current.y });
  }
  function up() {
    if (!start.current) return;
    const { x, y } = drag;
    start.current = null;
    let dir: SwipeDirection | null = null;
    if (y < -THRESHOLD && Math.abs(y) > Math.abs(x)) dir = "up";
    else if (y > THRESHOLD && Math.abs(y) > Math.abs(x)) dir = "down";
    else if (x > THRESHOLD) dir = "right";
    else if (x < -THRESHOLD) dir = "left";
    if (dir) fly(dir);
    else setDrag({ x: 0, y: 0 });
  }

  function fly(dir: SwipeDirection) {
    setLeaving(dir);
    setTimeout(() => onSwipe(dir), 240);
  }

  async function openTrailer() {
    if (trailerId) {
      setTrailerOpen(true);
      return;
    }
    if (trailerBusy) return;
    setTrailerBusy(true);
    try {
      const d = await api.title(token, card.tmdb_id);
      const id = d.trailer_url ? extractYouTubeId(d.trailer_url) : null;
      setTrailerId(id ?? "");
      if (id) setTrailerOpen(true);
    } catch {
      setTrailerId("");
    } finally {
      setTrailerBusy(false);
    }
  }

  const flyOff =
    leaving === "right"
      ? { x: 600, y: 40, rot: 24 }
      : leaving === "left"
        ? { x: -600, y: 40, rot: -24 }
        : leaving === "up"
          ? { x: 0, y: -800, rot: 0 }
          : leaving === "down"
            ? { x: 0, y: 800, rot: 0 }
            : null;

  const x = flyOff ? flyOff.x : drag.x;
  const y = flyOff ? flyOff.y : drag.y;
  const rot = flyOff ? flyOff.rot : drag.x / 18;
  const scale = interactive ? 1 : 1 - depth * 0.04;
  const ty = interactive ? 0 : depth * 10;

  // up = never, left = later, right = like, down = seen it.
  const likeOpacity = Math.max(0, Math.min(1, (x - 20) / 80)); // right
  const neverOpacity = Math.max(0, Math.min(1, (-y - 20) / 80)); // up
  const laterOpacity = Math.max(0, Math.min(1, (-x - 20) / 80)); // left
  const seenOpacity = Math.max(0, Math.min(1, (y - 20) / 80)); // down
  const stop = (e: React.PointerEvent | React.MouseEvent) => e.stopPropagation();

  return (
    <div
      className="swipecard"
      data-testid={interactive ? "swipe-card-top" : "swipe-card"}
      data-tmdb-id={card.tmdb_id}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
      style={{
        transform: interactive
          ? `translate(${x}px, ${y}px) rotate(${rot}deg)`
          : `translateY(${ty}px) scale(${scale})`,
        transition: start.current ? "none" : "transform 0.24s ease-out",
        zIndex: 10 - depth,
        cursor: interactive ? "grab" : "default",
      }}
    >
      <div className="poster" style={card.poster ? { backgroundImage: `url(${card.poster})` } : undefined}>
        {!card.poster && <div className="fallback">🎬</div>}
        {/* Scrim: transparent over the top quarter, dark over the lower ~75%. */}
        <div className="poster-scrim" />
        <RatingBadges ratings={card.ratings} />
        <div className="pref">match&nbsp;{Math.round(card.predicted_pref * 100)}%</div>
        {interactive && (
          <>
            <div className="stamp like" style={{ opacity: likeOpacity }}>
              Like
            </div>
            <div className="stamp never" style={{ opacity: neverOpacity }}>
              Nope
            </div>
            <div className="stamp later" style={{ opacity: laterOpacity }}>
              Later
            </div>
            <div className="stamp seen-it" style={{ opacity: seenOpacity }}>
              Seen it
            </div>
          </>
        )}
        <div className="info">
          <div data-testid="card-title" className="title">{card.title}</div>
          <div className="meta">
            {[card.year, card.runtime ? `${card.runtime} min` : null, card.genres.slice(0, 2).join(" · ")]
              .filter(Boolean)
              .join("  ·  ")}
          </div>
          {card.overview && (
            <div data-testid="card-overview" className="overview">
              {card.overview}
            </div>
          )}
          {interactive && trailerId !== "" && (
            <button
              className="card-trailer-btn"
              data-testid="card-trailer"
              onPointerDown={stop}
              onClick={(e) => {
                stop(e);
                openTrailer();
              }}
              aria-label="Watch trailer"
            >
              {trailerBusy ? "Loading…" : "▶ Trailer"}
            </button>
          )}
          <div className="chips">
            {card.available_on.map((o) => (
              <span className="chip svc" key={o.service}>
                {o.name}
              </span>
            ))}
          </div>
        </div>
        {interactive && onDetails && (
          <button
            className="card-details-btn"
            data-testid="card-details"
            onPointerDown={stop}
            onClick={(e) => { stop(e); onDetails(card.tmdb_id); }}
            aria-label="Show movie details"
          >
            ⓘ Details
          </button>
        )}
      </div>
      {trailerOpen && trailerId && <TrailerLightbox youtubeId={trailerId} onClose={() => setTrailerOpen(false)} />}
    </div>
  );
}
